import React, { useState, useEffect } from 'react';
import { AlertCircle } from 'lucide-react';
import axios from 'axios';
import Header from "../../../components/Header/Header";
import Footer from "../../../components/Footer/Footer";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

const StoreOfficerProjectAssignment = () => {
  const [storeOfficers, setStoreOfficers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedStoreOfficer, setSelectedStoreOfficer] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const officersResponse = await axios.get(`${API_BASE_URL}/storeoff/users`);
        const storeOfficersData = officersResponse.data.filter(
          (user) => user.Role === "Store Officer" && user.IsAuthorized
        );
        setStoreOfficers(storeOfficersData);

        const projectsResponse = await axios.get(`${API_BASE_URL}/storeoff/projects/unassigned`);
        setProjects(projectsResponse.data);

        setLoading(false);
      } catch (err) {
        setError("Failed to load data");
        console.error(err);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleAssignProject = async () => {
    if (!selectedStoreOfficer || !selectedProject) {
      setError("Please select both a store officer and a project");
      return;
    }

    try {
      await axios.put(`${API_BASE_URL}/storeoff/projects/assign`, {
        ProjectID: selectedProject,
        SOID: selectedStoreOfficer,
      });

      setProjects((prevProjects) =>
        prevProjects.filter((p) => p.ProjectID !== selectedProject)
      );
      setSelectedStoreOfficer(null);
      setSelectedProject(null);
      setError(null);
      alert("Project assigned successfully!");
    } catch (err) {
      setError("Failed to assign project");
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Header />

      <main className="flex-grow p-4">
        <div className="max-w-4xl mx-auto p-4 border rounded-lg shadow-md bg-white">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold">Store Officer Project Assignment</h2>
            <AlertCircle className="w-6 h-6 text-blue-500" />
          </div>

          {error && (
            <div
              className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4"
              role="alert"
            >
              <span className="block sm:inline">{error}</span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block mb-2 text-sm font-medium">
                Select Store Officer
              </label>
              <select
                value={selectedStoreOfficer || ""}
                onChange={(e) => {
                  setSelectedStoreOfficer(Number(e.target.value));
                  setError(null);
                }}
                className="w-full p-2 border rounded"
              >
                <option value="">Choose Store Officer</option>
                {storeOfficers.map((officer) => (
                  <option key={officer.UserID} value={officer.UserID}>
                    {officer.Username} ({officer.Email})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block mb-2 text-sm font-medium">Select Project</label>
              <select
                value={selectedProject || ""}
                onChange={(e) => {
                  setSelectedProject(Number(e.target.value));
                  setError(null);
                }}
                className="w-full p-2 border rounded"
              >
                <option value="">Choose Project</option>
                {projects.map((project) => (
                  <option key={project.ProjectID} value={project.ProjectID}>
                    {project.ProjectName} ({project.ProjectStage})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <button
            onClick={handleAssignProject}
            disabled={!selectedStoreOfficer || !selectedProject}
            className="w-full p-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300"
          >
            Assign Project
          </button>

          <div className="mt-6">
            <h3 className="text-lg font-semibold mb-4">Unassigned Projects</h3>
            {projects.length === 0 ? (
              <p className="text-gray-500 text-center">No unassigned projects</p>
            ) : (
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border p-2 text-left">Project Name</th>
                    <th className="border p-2 text-left">Project Stage</th>
                    <th className="border p-2 text-left">Entered By</th>
                  </tr>
                </thead>
                <tbody>
                  {projects.map((project) => (
                    <tr key={project.ProjectID} className="hover:bg-gray-50">
                      <td className="border p-2">{project.ProjectName}</td>
                      <td className="border p-2">{project.ProjectStage}</td>
                      <td className="border p-2">{project.EnteredBy}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default StoreOfficerProjectAssignment;
